import {
    VuexModule,
    Module,
    getModule,
    Mutation,
    Action,
} from 'vuex-module-decorators';
import store from '@/store';
import MetaDatatable from '@/models/MetaDatatable';
import { getProgramsPrescriber, postDenouncePrescriber, getProgramPrescriber } from '@/api/api';
import Program from '@/models/Program';
import FiltersModule from './FiltersModule';
import transformFilters from '@/utils/transformFilters';
import Lot from '@/models/Lot';
import uniqBy from 'lodash/uniqBy';
import DenounceRequest from '@/models/DenounceRequest';
import Denounce from '@/models/Denounce';
import DataDenounce from '@/models/DataDenounce';

function extractPrograms(lots: Lot[]): Program[] {
    const allPrograms = lots.map(lot => lot.program);
    // @ts-ignore
    return uniqBy(allPrograms, 'id');
}

@Module({ dynamic: true, store, name: 'list', namespaced: true })
class ListModule extends VuexModule {
    public lotsSelected: Lot[] = [];
    public loading: boolean = false;
    public programsBookmarked: Program[] = [];
    public programs: Program[] = [];
    public programsMax: number = 10;
    public meta: MetaDatatable = {
        total: 0,
        pages: 0,
        page: 0,
        perPage: 10,
        operators: null,
        search: null,
        orderBy: null,
        orderWay: null,
        filters: null,
        nbTotalLots: 0,
        nbTotalAllLots: 0
    };

    get programsSelected(): Program[] {
        return extractPrograms(this.lotsSelected);
    }

    get metaForApi() {
        const meta = { ...this.meta };
        const filters = transformFilters(FiltersModule.filters);
        if (filters.operator) {
            meta.operators = [filters.operator];
        } else {
            delete meta.operators;
        }
        return meta;
    }

    @Mutation
    setLotsSelected(lotsSelected: Lot[]): void {
        this.lotsSelected = lotsSelected;
    }

    @Action({ rawError: true })
    async selectLot(lot: Lot): Promise<void> {
        let lotsSelected = [...this.lotsSelected];

        const isLotSelected = (() => {
            const el = lotsSelected.find(x => x.id === lot.id);
            return el !== undefined;
        })();

        /*
        console.log(isLotSelected);
        */

        if (isLotSelected) {
            lotsSelected = lotsSelected.filter(item => item.id !== lot.id);
        } else {
            lotsSelected.push(lot);

            const programs = extractPrograms(lotsSelected);
            if (programs.length > this.programsMax) {
                this.context.commit('setLotsSelected', lotsSelected);
                throw new Error(
                    `Le maximum de programmes que l'on peut séléctionner est de ${this.programsMax
                    }.`
                );
            }
        }
        this.context.commit('setLotsSelected', lotsSelected);
    }

    @Mutation
    setProgramsBookmarked(programsBookmarked: Program[]): void {
        this.programsBookmarked = programsBookmarked;
    }

    @Mutation
    setMeta(meta: MetaDatatable) {
        this.meta = meta;
    }

    @Mutation
    setLoading(value: boolean) {
        this.loading = value;
    }

    @Mutation
    setPrograms(programs: Program[]) {
        this.programs = programs;
    }

    @Mutation
    addPrograms(programs: Program[]) {
        this.programs.push(...programs);
    }

    @Action
    setMetaAction(meta: MetaDatatable) {
        this.context.commit('setMeta', meta);
    }

    @Action
    async fetch(replace: boolean = false) {
        this.context.commit('setLoading', true);
        // console.log("Raw filters before transform:", FiltersModule.filters);
        const filters = transformFilters(FiltersModule.filters);
        // console.log("Transformed filters:", filters);
    
        const { data } = await getProgramsPrescriber({
            ...this.metaForApi,  // Utiliser metaForApi au lieu de this.meta
            page: replace ? 0 : this.meta.page,
            filters,
        });

        const meta = {
            total: data.meta.total || 0,
            pages: data.meta.pages || 0,
            page: data.meta.page || 0,
            perPage: data.meta.perPage || -1,
            search: data.meta.search || null,
            orderBy: data.meta.orderBy || null,
            orderWay: data.meta.orderWay || null,
            filters: data.meta.filters || null,
            nbTotalLots: data.meta.nbTotalLots || 0,
            nbTotalAllLots: data.meta.nbTotalAllLots || 0,
            operators: data.meta.operators || null
        };

        if (replace) {
            this.context.commit('setPrograms', data.data);
        } else {
            this.context.commit('addPrograms', data.data);
        }
        this.context.commit('setMeta', meta);
        this.context.commit('setLoading', false);
    }

    @Action
    async selectProgram(id: number) {
        let programsBookmarked = this.programsBookmarked;
        let programs = this.programs

        const programBookmarkedIndex = programsBookmarked.findIndex(program => program.id === id)

        if (programBookmarkedIndex > -1) {
            programsBookmarked.splice(programBookmarkedIndex, 1)

        } else {
            const programIndex = this.programs.findIndex(program => program.id === id)

            if (programIndex > -1) {
                programsBookmarked.push(this.programs[programIndex]);

            } else {
                const { data } = await getProgramPrescriber(id)
                programsBookmarked.push(data)
            }
        }

        this.context.commit('setProgramsBookmarked', programsBookmarked);
        this.context.commit('setPrograms', programs);
        this.context.commit('setLoading', false);
    }

    @Action({ rawError: true })

    async denounce(dataDenounce: any) {
        // console.log(dataDenounce.project, dataDenounce.file, dataDenounce.message_text, dataDenounce.prospectID);

        const lots: number[] = this.lotsSelected.map(p => p.id || -1);


        const request: DenounceRequest = {
            lots,
            project: dataDenounce.project,
            file: dataDenounce.file,
            message_text: dataDenounce.message_text,
            customer: dataDenounce.prospectID,
        };

        try {
            await postDenouncePrescriber(request);
            localStorage.customerIDDenouncedFresh = dataDenounce.prospectID
        } catch (error) {
            throw error;
        }
    }

    /**
     *
    ---------------
    to implement  !!!
     ---------------
     */
    // async postFtp(file: FormData) {
    //     await postDenounceFile(file);
    // }
}

export default getModule(ListModule);
